import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import {
  departmentOptions,
  roleLevelOptions,
  roleLevelBadge,
} from "@/mocks/employees";
import type { Employee } from "@/types/employee";

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("zh-TW", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function InfoField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs text-foreground-500 mb-1">{label}</p>
      <p className="text-sm text-foreground-900 font-medium">
        {value || <span className="text-foreground-400">—</span>}
      </p>
    </div>
  );
}

function ImagePreview({ label, url }: { label: string; url?: string | null }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <p className="text-xs text-foreground-500 mb-1.5">{label}</p>
      {url ? (
        <div
          className="relative group cursor-pointer rounded-lg border border-background-200 overflow-hidden"
          onClick={() => setOpen(true)}
        >
          <img
            src={url}
            alt={label}
            className="w-full h-32 object-cover"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
            <i className="ri-zoom-in-line text-white opacity-0 group-hover:opacity-100 text-xl transition-opacity" />
          </div>
        </div>
      ) : (
        <div className="w-full h-32 rounded-lg border border-dashed border-background-300 bg-background-50 flex flex-col items-center justify-center gap-1">
          <i className="ri-image-line text-foreground-300 text-xl" />
          <span className="text-xs text-foreground-400">未上傳</span>
        </div>
      )}
      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-black/70"
          onClick={() => setOpen(false)}
        >
          <img
            src={url!}
            alt={label}
            className="max-w-full max-h-[85vh] rounded-xl shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}

function EmployeeModal({
  mode,
  employee,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  mode: "create" | "edit";
  employee?: Employee | null;
  onClose: () => void;
  onSubmit: (data: Record<string, string>) => void;
  isSubmitting: boolean;
}) {
  const [formData, setFormData] = useState({
    name: employee?.name || "",
    email: employee?.email || "",
    phone: employee?.phone || "",
    department: employee?.department || "",
    position: employee?.position || "",
    role_level: employee?.role_level || "trial",
    initial_password: "000000",
  });
  const [formError, setFormError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setFormError("請輸入姓名");
      return;
    }
    if (!formData.email.trim()) {
      setFormError("請輸入 Email");
      return;
    }
    if (mode === "create" && !formData.initial_password.trim()) {
      setFormError("請輸入初始密碼");
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <div className={`relative bg-background-50 rounded-2xl w-full ${mode === "edit" ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-bold text-foreground-900">
              {mode === "create" ? "建立員工帳號" : "編輯員工資料"}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 cursor-pointer"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
          {mode === "create" && (
            <p className="text-xs text-foreground-500 mb-5">
              帳號將以「員工編號」自動產生，請將員工編號與初始密碼提供給員工。
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "create" && (
              <div>
                <label className="block text-xs text-foreground-600 mb-1.5">
                  帳號（員工編號・自動產生）
                </label>
                <input
                  type="text"
                  disabled
                  placeholder="建立後由系統指派"
                  className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-400 bg-background-100 cursor-not-allowed"
                />
              </div>
            )}

            {mode === "edit" && employee && (
              <div>
                <label className="block text-xs text-foreground-600 mb-1.5">
                  員工編號
                </label>
                <input
                  type="text"
                  value={employee.employee_code}
                  disabled
                  className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-400 bg-background-100 cursor-not-allowed"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-foreground-600 mb-1.5">
                  姓名 <span className="text-accent-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="例：陳小華"
                  className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-600 mb-1.5">
                  Email <span className="text-accent-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="user@company.com"
                  disabled={mode === "edit"}
                  className={`w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 ${
                    mode === "edit" ? "bg-background-100 cursor-not-allowed text-foreground-400" : ""
                  }`}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-foreground-600 mb-1.5">
                  電話
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="0912-345-678"
                  className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                />
              </div>
              {mode === "create" && (
                <div>
                  <label className="block text-xs text-foreground-600 mb-1.5">
                    初始密碼
                  </label>
                  <input
                    type="text"
                    name="initial_password"
                    value={formData.initial_password}
                    onChange={handleChange}
                    placeholder="000000"
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-foreground-600 mb-1.5">
                  部門
                </label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 appearance-none cursor-pointer"
                >
                  <option value="">請選擇</option>
                  {departmentOptions.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-foreground-600 mb-1.5">
                  職位
                </label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  placeholder="例：客服專員"
                  className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs text-foreground-600 mb-1.5">
                權限等級
              </label>
              <select
                name="role_level"
                value={formData.role_level}
                onChange={handleChange}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 appearance-none cursor-pointer"
              >
                {roleLevelOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {mode === "edit" && employee && (
              <>
                <hr className="my-5 border-background-200" />

                <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-1.5">
                  <i className="ri-user-line text-primary-500" />
                  個人資料
                </h3>
                <div className="grid grid-cols-2 gap-4 mb-5">
                  <InfoField label="地址" value={employee.address} />
                  <InfoField label="身分證字號" value={employee.id_card_number} />
                </div>

                <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-1.5">
                  <i className="ri-bank-card-line text-primary-500" />
                  銀行帳戶
                </h3>
                <div className="grid grid-cols-2 gap-4 mb-5">
                  <InfoField label="銀行名稱" value={employee.bank_name} />
                  <InfoField label="分行" value={employee.bank_branch} />
                  <InfoField label="銀行代碼" value={employee.bank_code} />
                  <InfoField label="戶名" value={employee.bank_account_name} />
                  <InfoField label="帳號" value={employee.bank_account_number} />
                </div>

                <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-1.5">
                  <i className="ri-image-line text-primary-500" />
                  證件照片
                </h3>
                <div className="grid grid-cols-2 gap-4 mb-5">
                  <ImagePreview label="身分證正面" url={employee.id_card_front_url} />
                  <ImagePreview label="身分證背面" url={employee.id_card_back_url} />
                  <ImagePreview label="銀行卡／存摺" url={employee.bank_card_image_url} />
                </div>

                <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-1.5">
                  <i className="ri-mail-check-line text-primary-500" />
                  信箱驗證狀態
                </h3>
                <div className="mb-2">
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${
                      employee.email_verified
                        ? "bg-primary-100 text-primary-700"
                        : "bg-accent-100 text-accent-700"
                    }`}
                  >
                    {employee.email_verified ? "已驗證" : "未驗證"}
                  </span>
                </div>
              </>
            )}

            {formError && (
              <div className="flex items-center gap-2 text-sm text-accent-600 bg-accent-50 px-3 py-2 rounded-lg">
                <i className="ri-error-warning-line" />
                {formError}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-1.5">
                    <i className="ri-loader-4-line animate-spin" />
                    {mode === "create" ? "建立中..." : "儲存中..."}
                  </span>
                ) : mode === "create" ? (
                  "建立並通知"
                ) : (
                  "儲存變更"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function EmployeesPage() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [deletingEmployee, setDeletingEmployee] = useState<Employee | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [fetchingDetail, setFetchingDetail] = useState(false);

  const showToast = (message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: fetchError } = await supabase
        .from("employees")
        .select("id, employee_code, name, email, department, position, role_level, created_at, status, avatar_url")
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;

      if (data && data.length > 0) {
        setEmployees(data as Employee[]);
      } else {
        setEmployees([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "載入失敗");
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const filteredEmployees = employees.filter((emp) => {
    const q = searchQuery.toLowerCase();
    return (
      emp.name.toLowerCase().includes(q) ||
      emp.email.toLowerCase().includes(q) ||
      emp.employee_code.toLowerCase().includes(q) ||
      (emp.department && emp.department.toLowerCase().includes(q))
    );
  });

  const handleCreate = async (formData: Record<string, string>) => {
    setIsSubmitting(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "create-employee",
        {
          body: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            department: formData.department,
            position: formData.position,
            role_level: formData.role_level,
            initial_password: formData.initial_password,
          },
        }
      );

      if (fnError) throw fnError;
      if (!data?.success) throw new Error(data?.error || "建立失敗");

      showToast(`員工 ${formData.name} 建立成功`, "success");
      setShowCreateModal(false);
      await fetchEmployees();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "建立失敗", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (formData: Record<string, string>) => {
    if (!editingEmployee) return;
    setIsSubmitting(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "update-employee",
        {
          body: {
            employee_id: editingEmployee.id,
            updates: {
              name: formData.name,
              phone: formData.phone || null,
              department: formData.department || null,
              position: formData.position || null,
              role_level: formData.role_level,
            },
          },
        }
      );

      if (fnError) throw fnError;
      if (!data?.success) throw new Error(data?.error || "更新失敗");

      showToast("員工資料更新成功", "success");
      setEditingEmployee(null);
      await fetchEmployees();
    } catch (err) {
      console.error("Save error:", err);
      const msg = err instanceof Error ? err.message : "更新失敗";
      showToast(msg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (emp: Employee) => {
    const newStatus = emp.status === "active" ? "inactive" : "active";
    const actionLabel = newStatus === "active" ? "啟用" : "停用";
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "update-employee",
        {
          body: {
            employee_id: emp.id,
            updates: {
              status: newStatus,
            },
          },
        }
      );

      if (fnError) throw fnError;
      if (!data?.success) throw new Error(data?.error || `${actionLabel}失敗`);

      showToast(`已${actionLabel} ${emp.name}`, "success");
      await fetchEmployees();
    } catch (err) {
      showToast(err instanceof Error ? err.message : `${actionLabel}失敗`, "error");
    }
  };

  const handleDelete = async () => {
    if (!deletingEmployee) return;
    setIsDeleting(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke(
        "delete-employee",
        { body: { employee_id: deletingEmployee.id } }
      );

      if (fnError) throw fnError;
      if (!data?.success) throw new Error(data?.error || "刪除失敗");

      showToast(data?.message || `${deletingEmployee.name} 已刪除`, "success");
      setDeletingEmployee(null);
      await fetchEmployees();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "刪除失敗", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchEmployeeDetail = async (empId: string) => {
    setFetchingDetail(true);
    try {
      const { data, error } = await supabase
        .from("employees")
        .select("*")
        .eq("id", empId)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        setEditingEmployee(data as Employee);
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : "載入員工資料失敗", "error");
    } finally {
      setFetchingDetail(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto space-y-5">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-20 right-6 z-50 px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm font-medium transition-all ${
            toast.type === "success"
              ? "bg-primary-500 text-background-50"
              : "bg-accent-500 text-background-50"
          }`}
        >
          <i
            className={
              toast.type === "success"
                ? "ri-check-line"
                : "ri-error-warning-line"
            }
          />
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1
            className="text-xl font-bold text-foreground-900"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            員工管理
          </h1>
          <p className="text-xs text-foreground-500 mt-1">
            員工總覽・建立／編輯／停用員工帳號（權限模組請至「權限設定」管理）
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" />
          建立員工帳號
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <i className="ri-search-line text-foreground-400 text-sm" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="搜尋姓名、Email、員工編號..."
          className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
        />
      </div>

      {/* Table */}
      <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 flex flex-col items-center gap-3">
            <i className="ri-loader-4-line animate-spin text-primary-500 text-2xl" />
            <p className="text-sm text-foreground-500">載入中...</p>
          </div>
        ) : error && employees.length === 0 ? (
          <div className="p-12 flex flex-col items-center gap-3">
            <i className="ri-error-warning-line text-accent-500 text-2xl" />
            <p className="text-sm text-foreground-500">{error}</p>
            <button
              onClick={fetchEmployees}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
            >
              重試
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-20">
                    員工編號
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">
                    姓名
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-20">
                    帳號
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-24">
                    部門
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-24">
                    職位
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-28">
                    權限
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-28">
                    建立日期
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-20">
                    狀態
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500 w-24">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((emp) => {
                  const badge = roleLevelBadge[emp.role_level] || {
                    bg: "bg-background-100",
                    text: "text-foreground-500",
                    label: emp.role_level,
                  };
                  return (
                    <tr
                      key={emp.id}
                      className="border-b border-background-100 hover:bg-background-50/60 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-md bg-background-100 text-foreground-700 text-xs font-medium">
                          {emp.employee_code}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-background-50 text-xs font-semibold shrink-0 overflow-hidden">
                            {emp.avatar_url ? (
                              <img
                                src={emp.avatar_url}
                                alt={emp.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              emp.name.charAt(0)
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground-900">
                              {emp.name}
                            </p>
                            <p className="text-xs text-foreground-400">
                              {emp.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-foreground-600">
                        {emp.employee_code}
                      </td>
                      <td className="px-4 py-3 text-foreground-600">
                        {emp.department || "—"}
                      </td>
                      <td className="px-4 py-3 text-foreground-600">
                        {emp.position || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${badge.bg} ${badge.text}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-500">
                        {formatDate(emp.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            emp.status === "active"
                              ? "bg-primary-100 text-primary-700"
                              : "bg-accent-100 text-accent-700"
                          }`}
                        >
                          {emp.status === "active" ? "啟用中" : "已停用"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => fetchEmployeeDetail(emp.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-500 cursor-pointer"
                            title="編輯"
                          >
                            <i className="ri-edit-line" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(emp)}
                            className={`w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer ${
                              emp.status === "active"
                                ? "text-accent-500"
                                : "text-primary-500"
                            }`}
                            title={
                              emp.status === "active" ? "停用" : "啟用"
                            }
                          >
                            <i
                              className={
                                emp.status === "active"
                                  ? "ri-forbid-line"
                                  : "ri-checkbox-circle-line"
                              }
                            />
                          </button>
                          <button
                            onClick={() => setDeletingEmployee(emp)}
                            className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-accent-50 text-accent-500 cursor-pointer"
                            title="刪除"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredEmployees.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-4 py-12 text-center text-foreground-400"
                    >
                      <i className="ri-user-search-line text-2xl mb-2 block" />
                      找不到符合條件的員工
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
          <i className="ri-information-line text-primary-500" />
          員工帳號建立流程
        </h3>
        <ol className="space-y-2 text-xs text-foreground-600">
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
              1
            </span>
            <span>
              超級管理員／管理者於「員工管理中心」點擊「建立員工帳號」
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
              2
            </span>
            <span>
              填寫姓名、帳號、初始密碼、電話、Email、部門、職位、權限等級
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
              3
            </span>
            <span>
              系統建立帳號，將帳號密碼提供給員工
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
              4
            </span>
            <span>
              員工首次登入後需強制完成「修改密碼＋個人資料＋身分證件＋薪資帳戶」四項設定
            </span>
          </li>
        </ol>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <EmployeeModal
          mode="create"
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreate}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Edit Modal */}
      {editingEmployee && (
        <EmployeeModal
          mode="edit"
          employee={editingEmployee}
          onClose={() => setEditingEmployee(null)}
          onSubmit={handleEdit}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingEmployee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !isDeleting && setDeletingEmployee(null)}
          />
          <div className="relative bg-background-50 rounded-2xl w-full max-w-md">
            <div className="p-6">
              <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-accent-500 text-xl" />
              </div>
              <h2 className="text-lg font-bold text-foreground-900 text-center mb-2">
                確認刪除員工
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-1">
                你即將刪除員工
              </p>
              <p className="text-sm font-semibold text-foreground-900 text-center mb-4">
                {deletingEmployee.name}（{deletingEmployee.email}）
              </p>
              <div className="bg-accent-50 border border-accent-200 rounded-lg p-3 mb-5">
                <p className="text-xs text-accent-700 flex items-start gap-2">
                  <i className="ri-alert-line shrink-0 mt-0.5" />
                  <span>此操作將永久刪除該員工帳號、所有關聯資料（貨運訂單、帳務記錄、打卡記錄等），且無法復原。</span>
                </p>
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeletingEmployee(null)}
                  disabled={isDeleting}
                  className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 rounded-lg bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
                >
                  {isDeleting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      刪除中...
                    </>
                  ) : (
                    <>
                      <i className="ri-delete-bin-line" />
                      確認刪除
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}